/**
 * PromotionsCard
 *
 * Dashboard card displaying upcoming promotions with status badges.
 * Allows creating, editing, and managing promotional campaigns.
 */

import React from 'react';
import {
    Tag,
    Plus,
    Calendar,
    Percent,
    DollarSign,
    Gift,
    Package,
    Truck,
    MoreVertical,
    Edit2,
    Trash2,
    ChevronRight,
    Infinity
} from 'lucide-react';
import {
    PROMOTION_TYPES,
    getPromotionStatus,
    formatDiscountValue
} from '../../services/promotionService';

// Icons for promotion types
const PROMOTION_TYPE_ICONS = {
    percentage: Percent,
    fixed: DollarSign,
    bogo: Gift,
    bundle: Package,
    free_shipping: Truck
};

const PromotionsCard = ({
    promotions = [],
    onCreatePromotion,
    onEditPromotion,
    onDeletePromotion,
    loading = false
}) => {
    // Sort promotions: ongoing/active first, then upcoming, then ended
    const sortedPromotions = [...promotions].sort((a, b) => {
        const statusOrder = { ongoing: 0, active: 0, upcoming: 1, ended: 2, inactive: 3 };
        const statusA = getPromotionStatus(a).status;
        const statusB = getPromotionStatus(b).status;

        if (statusOrder[statusA] !== statusOrder[statusB]) {
            return statusOrder[statusA] - statusOrder[statusB];
        }

        // Within same status, sort by start date (ongoing first if both same status)
        if (a.isOngoing && !b.isOngoing) return -1;
        if (!a.isOngoing && b.isOngoing) return 1;

        return new Date(a.startDate || 0) - new Date(b.startDate || 0);
    });

    // Show at most 5 promotions in the card
    const displayedPromotions = sortedPromotions.slice(0, 5);
    const hasMore = sortedPromotions.length > 5;

    const formatDateRange = (promotion) => {
        if (promotion.isOngoing) {
            return 'Ongoing';
        }

        const start = new Date(promotion.startDate);
        const end = new Date(promotion.endDate);
        const options = { month: 'short', day: 'numeric' };

        if (start.getFullYear() !== end.getFullYear()) {
            return `${start.toLocaleDateString('en-US', { ...options, year: 'numeric' })} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
        }

        return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
    };

    return (
        <div className="card" style={{ gridColumn: 'span 12' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-4)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Tag size={18} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Upcoming Promotions</span>
                    {promotions.length > 0 && (
                        <span style={{
                            background: 'var(--color-bg-secondary)',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            {promotions.length}
                        </span>
                    )}
                </div>
                <button
                    onClick={onCreatePromotion}
                    className="btn btn-primary btn-sm"
                >
                    <Plus size={14} />
                    Add Promotion
                </button>
            </div>

            {loading ? (
                <div style={{
                    padding: 'var(--space-6)',
                    textAlign: 'center',
                    color: 'var(--color-text-secondary)'
                }}>
                    Loading promotions...
                </div>
            ) : displayedPromotions.length === 0 ? (
                <div style={{
                    padding: 'var(--space-6)',
                    textAlign: 'center',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    <Tag size={32} style={{ color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }} />
                    <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                        No promotions scheduled. Create one to include discount messaging in your content.
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {displayedPromotions.map((promotion) => {
                        const status = getPromotionStatus(promotion);
                        const Icon = PROMOTION_TYPE_ICONS[promotion.type] || Tag;

                        return (
                            <PromotionRow
                                key={promotion.id}
                                promotion={promotion}
                                status={status}
                                Icon={Icon}
                                formatDateRange={formatDateRange}
                                onEdit={() => onEditPromotion(promotion)}
                                onDelete={() => onDeletePromotion(promotion)}
                            />
                        );
                    })}

                    {hasMore && (
                        <div style={{
                            textAlign: 'center',
                            padding: 'var(--space-2)',
                            color: 'var(--color-text-secondary)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            +{sortedPromotions.length - 5} more promotions
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const PromotionRow = ({ promotion, status, Icon, formatDateRange, onEdit, onDelete }) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const isActiveOrOngoing = status.status === 'active' || status.status === 'ongoing';

    return (
        <div
            onClick={onEdit}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3)',
                background: isActiveOrOngoing ? 'var(--success-50)' : 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'background var(--transition-fast)',
                borderLeft: `3px solid ${status.color}`
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = isActiveOrOngoing ? 'var(--success-100)' : 'var(--color-bg-tertiary)'}
            onMouseLeave={(e) => e.currentTarget.style.background = isActiveOrOngoing ? 'var(--success-50)' : 'var(--color-bg-secondary)'}
        >
            <div style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-bg-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                <Icon size={18} style={{ color: status.color }} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {promotion.name}
                    </span>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-full)',
                        background: isActiveOrOngoing ? 'var(--success-100)' : 'var(--color-bg-tertiary)',
                        color: status.color,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        {promotion.isOngoing && <Infinity size={10} />}
                        {status.label}
                    </span>
                </div>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    {promotion.isOngoing ? <Infinity size={12} /> : <Calendar size={12} />}
                    {formatDateRange(promotion)}
                </div>
            </div>

            <div style={{
                fontWeight: 600,
                color: 'var(--color-primary)',
                whiteSpace: 'nowrap'
            }}>
                {formatDiscountValue(promotion)}
            </div>

            {promotion.discountCode && (
                <div style={{
                    padding: '2px 8px',
                    background: 'var(--color-bg-primary)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'monospace',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    border: '1px dashed var(--color-border)'
                }}>
                    {promotion.discountCode}
                </div>
            )}

            <div style={{ position: 'relative' }}>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(!showMenu);
                    }}
                    className="btn btn-ghost btn-sm"
                    style={{ padding: 'var(--space-1)' }}
                >
                    <MoreVertical size={16} />
                </button>

                {showMenu && (
                    <>
                        <div
                            style={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 99
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowMenu(false);
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            background: 'var(--color-bg-primary)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-lg)',
                            zIndex: 100,
                            minWidth: '120px',
                            overflow: 'hidden'
                        }}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMenu(false);
                                    onEdit();
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    width: '100%',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-primary)',
                                    textAlign: 'left'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                                <Edit2 size={14} />
                                Edit
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMenu(false);
                                    onDelete();
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    width: '100%',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--error-600)',
                                    textAlign: 'left'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--error-50)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                                <Trash2 size={14} />
                                Delete
                            </button>
                        </div>
                    </>
                )}
            </div>

            <ChevronRight size={16} style={{ color: 'var(--color-text-tertiary)' }} />
        </div>
    );
};

export default PromotionsCard;
